/**
 * Equation for personalized nutrition
 * Accounts for fat loss (deficit) vs muscle gain (surplus) scenarios
 * Uses activity level for accurate TDEE calculation
 */
export function calculateUserTargets(profile) {
  // Guard against null/undefined profile FIRST
  if (!profile || typeof profile !== "object") {
    return { calories: "--", protein: "--" };
  }

  const { weight, height, age, goalWeight, activityLevel } = profile;

  // If data isn't set yet, return safe defaults to prevent NaN errors
  if (!weight || !height || !age || !activityLevel) {
    return { calories: "--", protein: "--" };
  }

  // 1. Calculate BMR (Basal Metabolic Rate)
  const bmr = 10 * weight + 6.25 * height - 5 * age + 5;

  // 2. Activity Level Multiplier
  const activityMultipliers = {
    sedentary: 1.2, // Little/no exercise
    lightly_active: 1.375, // 1-3x/week
    moderately_active: 1.55, // 3-5x/week
    very_active: 1.725, // 6-7x/week
  };

  const activityMultiplier = activityMultipliers[activityLevel] || 1.4;

  // 3. TDEE (Maintenance) - Based on activity level
  const maintenance = bmr * activityMultiplier;

  // 4. Determine deficit/surplus based on goal weight
  let adjustment = 0;
  let scenario = "maintenance";

  if (goalWeight) {
    const weightDifference = goalWeight - weight;
    const percentDifference = Math.abs(weightDifference) / weight;

    if (weightDifference < 0) {
      // User needs to LOSE weight (deficit)
      scenario = "fat_loss";
      // Standard 500 cal deficit, or higher if large deficit needed
      adjustment = -500;

      // If significant weight loss needed (>10%), consider moderate deficit
      if (percentDifference > 0.1) {
        adjustment = -750; // More aggressive deficit
      }
    } else if (weightDifference > 0) {
      // User needs to GAIN weight (surplus)
      scenario = "muscle_gain";
      // Use surplus instead of deficit
      adjustment = 500; // 500 cal surplus for muscle gain

      // If significant weight gain needed (>10%), use larger surplus
      if (percentDifference > 0.1) {
        adjustment = 750; // Larger surplus for more muscle growth
      }
    }
  } else {
    // No goal weight provided, default to conservative deficit
    scenario = "fat_loss";
    adjustment = -500;
  }

  // 5. Calculate target calories
  const targetCals = Math.round(maintenance + adjustment);

  // 6. Protein Target
  // During fat loss: 2.2g per kg (preserve muscle)
  // During muscle gain: 2g per kg (support growth)
  // Maintenance: 1.8g per kg
  let proteinMultiplier = 1.8;

  if (scenario === "fat_loss") {
    proteinMultiplier = 2.2;
  } else if (scenario === "muscle_gain") {
    proteinMultiplier = 2.0;
  }

  const targetProtein = Math.round(weight * proteinMultiplier);

  return {
    calories: targetCals,
    protein: targetProtein,
    maintenance,
    bmr,
    scenario,
    adjustmentValue: adjustment,
    activityLevel,
    activityMultiplier,
  };
}

/**
 * Example usage:
 *
 * // User needs to LOSE weight (93 → 85) - Moderately Active
 * calculateUserTargets({
 *   weight: 93,
 *   height: 180,
 *   age: 28,
 *   goalWeight: 85,
 *   activityLevel: "moderately_active"
 * });
 * // Returns: { calories: ~1700-1800, protein: ~200, scenario: 'fat_loss', activityMultiplier: 1.55, ... }
 *
 * // User needs to GAIN weight (75 → 85) - Very Active
 * calculateUserTargets({
 *   weight: 75,
 *   height: 180,
 *   age: 28,
 *   goalWeight: 85,
 *   activityLevel: "very_active"
 * });
 * // Returns: { calories: ~2300-2400, protein: ~150, scenario: 'muscle_gain', activityMultiplier: 1.725, ... }
 *
 * // Activity Level Multipliers:
 * // - sedentary: 1.2 (BMR × 1.2)
 * // - lightly_active: 1.375 (BMR × 1.375)
 * // - moderately_active: 1.55 (BMR × 1.55)
 * // - very_active: 1.725 (BMR × 1.725)
 */
